import type { CapturedError, Environment } from '../shared/types.ts';

const MAX_ERRORS = 20;
const errors: CapturedError[] = [];
let installed = false;

function push(error: CapturedError): void {
  errors.push(error);
  if (errors.length > MAX_ERRORS) errors.shift();
}

/**
 * Remembers uncaught errors of the host page so reports carry them automatically.
 * Only listens – never wraps console or fetch of the host application.
 */
export function installErrorBuffer(): () => void {
  if (installed) return () => {};
  installed = true;
  const onError = (event: ErrorEvent) => {
    push({
      message: String(event.message || event.error || 'Unbekannter Fehler').slice(0, 500),
      source: event.filename || undefined,
      line: event.lineno || undefined,
      column: event.colno || undefined,
      time: new Date().toISOString(),
    });
  };
  const onRejection = (event: PromiseRejectionEvent) => {
    const reason = event.reason as unknown;
    const message = reason instanceof Error ? `${reason.name}: ${reason.message}` : String(reason);
    push({ message: `Unhandled rejection: ${message}`.slice(0, 500), time: new Date().toISOString() });
  };
  window.addEventListener('error', onError);
  window.addEventListener('unhandledrejection', onRejection);
  return () => {
    window.removeEventListener('error', onError);
    window.removeEventListener('unhandledrejection', onRejection);
    installed = false;
  };
}

export function capturedErrors(): CapturedError[] {
  return errors.slice();
}

export function appNameHint(): string | undefined {
  const meta =
    document.querySelector<HTMLMetaElement>('meta[name="application-name"]') ??
    document.querySelector<HTMLMetaElement>('meta[property="og:site_name"]') ??
    document.querySelector<HTMLMetaElement>('meta[name="apple-mobile-web-app-title"]');
  return meta?.content?.trim() || undefined;
}

export function collectEnvironment(widgetVersion: string): Environment {
  let timezone: string | undefined;
  try {
    timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  } catch {
    timezone = undefined;
  }
  return {
    userAgent: navigator.userAgent,
    language: navigator.language,
    languages: navigator.languages ? [...navigator.languages].slice(0, 5) : undefined,
    platform: (navigator as Navigator & { userAgentData?: { platform?: string } }).userAgentData?.platform ?? navigator.platform,
    timezone,
    viewport: { width: window.innerWidth, height: window.innerHeight },
    screen: { width: window.screen.width, height: window.screen.height },
    devicePixelRatio: window.devicePixelRatio || 1,
    colorScheme: window.matchMedia?.('(prefers-color-scheme: dark)').matches ? 'dark' : 'light',
    online: navigator.onLine,
    appNameHint: appNameHint(),
    widgetVersion,
  };
}

/** Short, human-readable browser name for the "what is sent" preview. */
export function browserName(ua = navigator.userAgent): string {
  const checks: Array<[RegExp, string]> = [
    [/Edg\/(\d+)/, 'Edge'],
    [/OPR\/(\d+)/, 'Opera'],
    [/Firefox\/(\d+)/, 'Firefox'],
    [/Chrome\/(\d+)/, 'Chrome'],
    [/Version\/(\d+).*Safari/, 'Safari'],
  ];
  for (const [re, name] of checks) {
    const m = re.exec(ua);
    if (m) return `${name} ${m[1]}`;
  }
  return ua.slice(0, 60);
}
